import { memo, useEffect, useState } from 'react'
import { useDisplayReports } from '../../app/home/displayReport'
import { fmtTemp } from '../../shared/units'
import { usePlaces } from '../store'
import type { ZoneProps } from '../data/types'
import { getTimeModels } from '../../app/time/hourModels'
import { getFrames } from '../formatters'
import { prepareConditions } from '../zones/conditions/readings'
import { prepareFusion } from '../zones/fusion/readings'
import { prepareHero } from '../zones/hero/Hero'
import { getPanorama } from '../zones/panorama/geometry'
import { afterPaint } from './afterPaint'
import { atIdle } from '../zones/radar/idle'

/** Two contained first-screen previews; no cursor subscription or new store. Prepared outside input handlers. */
export const NeighbourPlaces = memo(function NeighbourPlaces({ data }: ZoneProps) {
  const places = usePlaces()
  const reports = useDisplayReports(places)
  const [warm, setWarm] = useState(false)
  useEffect(() => {
    const at = places.findIndex((place) => place.id === data.place.id)
    const pending = [-1, 1].map((direction) => reports[(at + direction + places.length) % places.length]?.data).filter((report) => !!report)
    let cancel = () => {}
    const stages: (() => void)[] = []
    const channel = new MessageChannel()
    channel.port1.onmessage = () => { cancel = atIdle(run, 0) }
    const run = () => {
      if (stages.length) stages.shift()!()
      else {
        const report = pending.shift()
        if (report) {
          let models: ReturnType<typeof getTimeModels>, prepared: typeof data
          stages.push(
            () => { models = getTimeModels(report, data.units, data.mock ? Date.parse(report.current.time) : Date.now()) },
            () => { const frames = getFrames(report, models, data.units, data.mock); prepared = { ...data, report, models, now: frames.now, frames: frames.hours, error: null } },
            () => { prepareHero(prepared) }, () => { getPanorama(models.hours, report, data.units) },
            () => { prepareConditions(prepared) }, () => { prepareFusion(prepared) },
          )
        }
      }
      // Each stage gets its own idle task, returning to input between model/zone preparations.
      if (pending.length || stages.length) channel.port2.postMessage(null)
    }
    const cancelPaint = afterPaint(() => { cancel = atIdle(run) })
    return () => { cancelPaint(); cancel(); channel.port1.close(); channel.port2.close() }
  }, [places, reports, data.units, data.mock, data.place.id])
  useEffect(() => {
    const timer = window.setTimeout(() => setWarm(true), 700)
    return () => window.clearTimeout(timer)
  }, [])
  const index = places.findIndex((place) => place.id === data.place.id)
  return <div className="v5-neighbours" aria-hidden="true" inert data-qa-ignore>{[-1, 1].map((direction) => {
    const at = (index + direction + places.length) % places.length
    const place = places[at], report = reports[at]?.data
    return <div key={direction} className="v5-neighbour" data-side={direction} data-place={place.id} data-ready={warm && !!report}><h2>{place.name}</h2><strong>{warm ? fmtTemp(report?.current.temperature_c, data.units.temp) : '—'}</strong><p>{warm ? report?.current.description : ''}</p><p>{warm ? report?.summaries.today : ''}</p><div className="v5-neighbour-hours">{warm ? report?.hourly.slice(0, 12).map((h) => <span key={h.time}>{fmtTemp(h.temperature_c, data.units.temp)}</span>) : null}</div></div>
  })}</div>
})
